import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../../Services/translation';
import { cn } from '../../Helpers/cn';
import OrderStatus from '../primitives/OrderStatus';
import { parseAsBoolean, useQueryState } from 'nuqs';
import SvgIcon from '../primitives/SvgIcon';
import {
  cancelResellerOrder,
  query,
  sendResellerOrderReminder,
  updateResellerOrder,
} from '../../Actions/Order.action';
import LoaderOverlay from '../primitives/LoaderOverlay';
import ReorderOrderLineButtonContainer from '../../Containers/ReorderOrderLineButton.container';
import ApproveOrderButton from './ApproveOrderButton';
import ScrollableContainer from '../primitives/ScrollableContainer';

const formatActivityDate = (dateString) => {
  const date = new Date(dateString);
  const pad = (value) => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} · ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const withdrawalStatusClasses = {
  New: 'border-blue-700 bg-blue-100 text-blue-700',
  InProgress: 'border-yellow-700 bg-yellow-100 text-yellow-700',
  Completed: 'border-green-700 bg-green-100 text-green-700',
  Cancelled: 'border-gray-600 bg-gray-100 text-gray-600',
  Unknown: 'border-red-700 bg-red-100 text-red-700',
};

const OrderHistoryB2BDetail = ({
  onDismiss,
  isReseller,
  printPageBaseUrl,
  hasApproverRole,
  onApproveOrderCallback,
}) => {
  const dispatch = useDispatch();
  const { order, requestStatus, currentPage } = useSelector(
    (state) => state.myPage.orders
  );
  const { tabsConstants } = useSelector((state) => state.myPage);
  const [isPrintMode] = useQueryState(
    'print',
    parseAsBoolean.withDefault(false)
  );
  const [searchQuery] = useQueryState('searchQuery');
  const [statusFilter] = useQueryState('statusFilter');

  const isLoading = requestStatus === 'idle';

  const updateStatus = (newStatus) => {
    dispatch(updateResellerOrder(order?.id || order?.orderId, newStatus));
    dispatch(
      query(
        {
          pageIndex: currentPage,
          searchQuery,
          statusFilter,
        },
        { isReseller: true, showOrderDetail: true }
      )
    );
  };

  const cancelOrder = () => {
    dispatch(cancelResellerOrder(order?.id || order?.orderId));
    dispatch(
      query(
        {
          pageIndex: currentPage,
          searchQuery,
          statusFilter,
        },
        { isReseller: true, showOrderDetail: true }
      )
    );
  };

  useEffect(() => {
    if (isPrintMode && order) {
      const timer = setTimeout(() => {
        window.print();
      }, 1000); // Delay to ensure content is fully rendered before printing

      return () => clearTimeout(timer);
    }
  }, [isPrintMode, order]);

  const [showArroveOrderButton, setShowArroveOrderButton] = useState(false);

  useEffect(() => {
    setShowArroveOrderButton(hasApproverRole && order?.isWaitingApprove);
  }, [hasApproverRole, order?.isWaitingApprove]);

  const approveOrderCallback = useCallback(() => {
    onApproveOrderCallback && onApproveOrderCallback(order?.orderId, true);
  }, [onApproveOrderCallback, order?.orderId]);

  return (
    <Fragment>
      {!isPrintMode && (
        <div className="mb-4 flex flex-col justify-between md:flex-row">
          <button
            className="order-link-inverted inline-flex items-center gap-1 font-extrabold"
            onClick={onDismiss}
            type="button"
          >
            <span className="icon icon-outlined">chevron_left</span>
            {translate('orderdetail.backtoorderlist')}
          </button>
          {!isReseller && (
            <a
              className="order-link-inverted inline-flex items-center gap-1 font-extrabold"
              target="_blank"
              rel="noreferrer"
              href={`${printPageBaseUrl}?id=${
                order?.id || order?.orderId
              }&currentTab=${
                isReseller
                  ? tabsConstants.customerOrderDetail
                  : tabsConstants.orderDetail
              }&print=true`}
            >
              <span className="icon icon-outlined">print</span>
              {translate('general.print')}
            </a>
          )}
        </div>
      )}

      <div className="relative grid gap-4 md:grid-cols-2">
        {isLoading && <LoaderOverlay />}

        <div className="min-w-0">
          {isReseller && !isPrintMode && (
            <div className="page-content mb-4 flex h-fit flex-col gap-8">
              <SectionTitle>{translate('mypage.manage_order')}</SectionTitle>

              <ScrollableContainer
                initialScrollEnabled={true}
                className="category-list flex justify-center gap-2"
                scrollStep={200}
              >
                <StatusButtonItem
                  onClick={() => updateStatus('ProcessingReseller')}
                  disabled={!order.canSetAsProcessing}
                  label={translate('product.confirmed')}
                  icon="verified"
                  active={order.canSetAsReady}
                />
                <StatusButtonItem
                  onClick={() => updateStatus('ReadyReseller')}
                  disabled={!order.canSetAsReady}
                  label={translate('product.ready_to_pick_up')}
                  icon={<SvgIcon.pickup title="Ready for pickup" />}
                  active={order.canSetAsCompleted}
                />
                <StatusButtonItem
                  onClick={() => updateStatus('CompletedReseller')}
                  disabled={!order.canSetAsCompleted}
                  label={translate('product.delivered')}
                  icon={<SvgIcon.delivered title="Delivered" />}
                  active={
                    order.orderStatus === 'Completed' &&
                    order.statusColor !== 'red'
                  }
                />
              </ScrollableContainer>

              <ul className="flex flex-col gap-5">
                {showArroveOrderButton && (
                  <li>
                    <ApproveOrderButton
                      label={translate('approve.label')}
                      cssClass="flex items-center gap-2 group/btn font-extrabold text-sm"
                      orderId={order?.orderId}
                      callback={approveOrderCallback}
                    />
                  </li>
                )}
                <li>
                  <button
                    type="button"
                    className="group/btn flex items-center gap-2 text-sm font-extrabold"
                    onClick={() =>
                      dispatch(
                        sendResellerOrderReminder(order?.id || order?.orderId)
                      )
                    }
                  >
                    <span className="icon icon-outlined">outgoing_mail</span>
                    <span className="group-hover/btn:underline">
                      {translate('product.placeholder')}
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="group/btn flex items-center gap-2 text-sm font-extrabold"
                    onClick={() => dispatch(cancelOrder())}
                  >
                    <span className="icon icon-outlined">dangerous</span>
                    <span className="group-hover/btn:underline">
                      {translate('general.cancel')}
                    </span>
                  </button>
                </li>
                <li>
                  <a
                    className="order-link-inverted inline-flex items-center gap-2 text-sm font-extrabold"
                    target="_blank"
                    rel="noreferrer"
                    href={`${printPageBaseUrl}?id=${
                      order?.id || order?.orderId
                    }&currentTab=${
                      isReseller
                        ? tabsConstants.customerOrderDetail
                        : tabsConstants.orderDetail
                    }&print=true`}
                  >
                    <span className="icon icon-outlined">print</span>
                    {translate('general.print')}
                  </a>
                </li>
              </ul>
            </div>
          )}
          <div className="page-content h-fit">
            <SectionTitle>{translate('orderdetail.information')}</SectionTitle>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="lg:order-1">
                <Label title={translate('orderdetail.organizationnumber')}>
                  {order?.merchantOrganizationNumber}
                </Label>
                <Label
                  title={translate('orderdetail.billingdetails')}
                  direction="column"
                >
                  <Label element="span">{order?.companyName}</Label>
                  <Label element="span">{order?.companyAddress}</Label>
                  <Label element="span">
                    <a href={`mailto:${order?.companyEmail}`} className="link">
                      {order?.companyEmail}
                    </a>
                  </Label>
                  <Label element="span">
                    <a href={`tel:${order?.companyPhone}`} className="link">
                      {order?.companyPhone}
                    </a>
                  </Label>
                </Label>
              </div>

              <Label
                title={translate('orderdetail.deliverydetails')}
                direction="column"
                className="lg:order-3"
                element="div"
              >
                <Label element="span">
                  {order?.customerInfo?.firstName}{' '}
                  {order?.customerInfo?.lastName}
                </Label>
                <Label element="span"> {order?.customerInfo?.email} </Label>
                <Label element="span">
                  {' '}
                  {order?.customerInfo?.mobilePhone}{' '}
                </Label>
                <Label element="span">{order?.customerInfo?.address1}</Label>
                <Label element="span">
                  {order?.customerInfo?.zip} {order?.customerInfo?.city}
                </Label>
                <Label element="span">{order?.customerInfo?.country}</Label>
              </Label>

              <div className="order-1 flex flex-col gap-4 lg:order-2">
                <div className="flex flex-col lg:items-end lg:text-right">
                  <Label
                    title={translate('orderdetail.ordernumber')}
                    className="lg:justify-end"
                  >
                    {order?.externalOrderID}
                  </Label>
                  <Label
                    title={translate('orderdetail.orderdate')}
                    className="lg:justify-end"
                  >
                    {new Date(order.orderDate).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </Label>
                  <Label
                    title={translate('orderdetail.orderstatus')}
                    className="lg:justify-end"
                  >
                    {order?.isWaitingApprove && (
                      <OrderStatus
                        status={translate('order.state.waitingconfirmation')}
                        color={'blue'}
                      />
                    )}
                    {!order?.isWaitingApprove && (
                      <OrderStatus
                        status={order.status}
                        color={order.statusColor}
                      />
                    )}
                  </Label>
                </div>
                <div className="flex flex-col lg:items-end lg:text-right">
                  <Label
                    title={translate('orderdetail.paymentmethod')}
                    className="lg:justify-end"
                  >
                    {order?.paymentMethod}
                  </Label>
                  <Label
                    title={translate('orderdetail.deliverymethod')}
                    className="lg:justify-end"
                  >
                    {order?.deliveryMethod}
                  </Label>
                  {order?.formattedActualDeliveryDate && (
                    <Label
                      title={translate('orderdetail.deliverydate')}
                      className="lg:justify-end"
                    >
                      {order?.formattedActualDeliveryDate}
                    </Label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {order?.withdrawals?.length > 0 && (
            <div className="page-content mt-4 flex h-fit flex-col">
              <SectionTitle>
                {translate('orderdetail.reseller.withdrawals')}
              </SectionTitle>
              <div className="flex flex-col gap-4">
                {order.withdrawals.map((withdrawal) => (
                  <div
                    key={withdrawal.id}
                    className="rounded-radius border-border-primary overflow-hidden border"
                  >
                    <div className="bg-background-tertiary border-border-primary flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b px-4 py-2">
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        <strong className="text-[15px]">{withdrawal.id}</strong>
                        {withdrawal.createdAt && (
                          <span className="text-xs text-[#757575]">
                            {formatActivityDate(withdrawal.createdAt)}
                          </span>
                        )}
                      </div>
                      <span
                        className={cn(
                          'rounded-full border px-2.5 py-0.5 text-sm font-bold',
                          withdrawalStatusClasses[
                            withdrawal.processingStatusName
                          ] ?? withdrawalStatusClasses.Unknown
                        )}
                      >
                        {withdrawal.processingStatusName}
                      </span>
                    </div>
                    <div className="px-4 py-3">
                      <table className="w-full max-w-full border-collapse text-left text-[15px]">
                        <thead>
                          <tr className="border-border-primary border-b">
                            <th className="w-full max-w-0 truncate pb-2 pr-4 font-bold">
                              {translate('orderdetail.withdrawal.product')}
                            </th>
                            <th className="whitespace-nowrap pb-2 pr-4 font-bold">
                              {translate(
                                'orderdetail.withdrawal.productnumber'
                              )}
                            </th>
                            <th className="whitespace-nowrap pb-2 text-right font-bold">
                              {translate('orderdetail.withdrawal.quantity')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(withdrawal.rows ?? []).map((row) => (
                            <tr
                              key={row.articleNumber}
                              className="border-border-primary border-b last:border-b-0"
                            >
                              <td className="w-full max-w-0 truncate py-2 pr-4">
                                {
                                  order.orderRows?.find(
                                    (orderRow) =>
                                      orderRow.articleNumber ===
                                      row.articleNumber
                                  )?.name
                                }
                              </td>
                              <td className="whitespace-nowrap py-2 pr-4">
                                {row.articleNumber}
                              </td>
                              <td className="whitespace-nowrap py-2 text-right">
                                {row.quantity}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="page-content flex h-fit flex-col overflow-clip">
            <SectionTitle>{translate('orderdetail.summary')}</SectionTitle>
            <div className="mb-8">
              {order?.orderRows?.map((row, index) => (
                <div
                  key={index}
                  className="border-border-primary @container mb-4 flex border-b pb-4 text-sm"
                >
                  <div className="bg-background-tertiary aspect-square w-[88px] min-w-[88px]">
                    <img
                      src={row.image}
                      alt={row.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div
                    className={cn(
                      'ml-4 flex flex-1 flex-col justify-center gap-2'
                    )}
                  >
                    <div className="@md:flex-row @md:justify-between flex flex-col gap-1">
                      <div className="flex flex-col gap-1">
                        <a
                          href={row.link.href}
                          className="link-inverted font-semibold"
                        >
                          {row.name}
                        </a>
                        {row?.articleNumber && (
                          <p className="text-accent text-xs">
                            {row.articleNumber}
                          </p>
                        )}
                        <p className="text-[#757575]">{row.quantityString}</p>
                      </div>
                      <p>{row.totalPrice}</p>
                    </div>
                    {isReseller && (
                      <ReorderOrderLineButtonContainer
                        quantity={row.quantity}
                        title={translate('product.buy')}
                        label={
                          <SvgIcon.basket title={translate('product.buy')} />
                        }
                        cssClass="flex justify-between items-center"
                        articleNumber={row.articleNumber}
                        cssButtonClass={
                          'button p-0 m-0 cursor-pointer bg-accent text-white flex items-center justify-center size-12 text-center rounded-full'
                        }
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-background-tertiary -mx-4 -mb-4 mt-auto p-4 md:-mx-6 md:-mb-6 md:p-6 lg:-mx-8 lg:-mb-8 lg:p-8">
              {order?.orderTotalDiscountAmount && (
                <SummaryRow
                  label={translate('orderdetail.discount')}
                  value={order?.orderTotalDiscountAmount}
                />
              )}
              {order?.discountRows?.length !== 0 && (
                <ul className="mb-4 ml-2">
                  {order?.discountRows?.map((row) => (
                    <SummaryDetailsRow
                      key={row.name}
                      label={row.name}
                      value={row.totalPrice}
                    />
                  ))}
                </ul>
              )}
              {order?.orderTotal && (
                <SummaryRow
                  label={translate('orderdetail.totalexcludingvat')}
                  value={order?.orderTotal}
                />
              )}
              {order?.orderTotalVat && (
                <SummaryRow
                  label={translate('orderdetail.ordertotalvat')}
                  value={order?.orderTotalVat}
                />
              )}
              {order?.orderGrandTotal && (
                <SummaryRow
                  label={translate('orderdetail.grandtotal')}
                  value={order?.orderGrandTotal}
                  className="text-xl font-extrabold"
                />
              )}
            </div>
          </div>

          {order?.activities?.length > 0 && (
            <div className="page-content mt-4 flex h-fit flex-col">
              <SectionTitle>
                {translate('orderdetail.reseller.orderactivities')}
              </SectionTitle>
              <ul>
                {[...(order?.activities ?? [])].map((activity, index) => (
                  <li
                    key={index}
                    className="border-border-primary border-b pb-3 last:border-b-0 last:pb-0 [&:not(:last-child)]:mb-3"
                  >
                    <p>{activity.text}</p>
                    <p
                      className="text-xs text-[#757575]"
                      title={formatActivityDate(activity.atUtc)}
                    >
                      {formatActivityDate(activity.atUtc)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* <div className="row-inner order-detail__button-container">
          <div className="small-6">
            {showArroveOrderButton && (
              <ApproveOrderButton
                label={translate('approve.label')}
                cssClass="order-detail__button"
                orderId={order?.orderId}
                callback={approveOrderCallback}
              />
            )}
            <ReorderButton
              label={translate('general.reorder')}
              cssClass="order-detail__button"
              orderId={order?.orderId}
            />
          </div>
        </div> */}
    </Fragment>
  );
};

export default OrderHistoryB2BDetail;

const SectionTitle = ({ children, className }) => {
  return (
    <h3 className={cn('text-accent mb-8 text-xl font-extrabold', className)}>
      {children}
    </h3>
  );
};

const LabelTitle = ({ children, className, element }) => {
  const Wrapper = element || 'div';
  return (
    <Wrapper className={cn('text-[15px] font-bold leading-normal', className)}>
      {children}
    </Wrapper>
  );
};

const Label = ({
  children,
  className,
  title,
  direction = 'row',
  element = 'p',
}) => {
  const Wrapper = element;
  if (!children) return null;

  return (
    <Wrapper
      className={cn(
        'wrap-anywhere hyphens-auto text-[15px] leading-normal',
        title ? 'mb-2' : '',
        direction === 'column'
          ? 'flex flex-col gap-1'
          : 'flex flex-row flex-wrap gap-1',
        className
      )}
    >
      {title ? (
        <>
          <LabelTitle element="span">{title}: </LabelTitle>
          <span>{children}</span>
        </>
      ) : (
        <span>{children}</span>
      )}
    </Wrapper>
  );
};

const SummaryRow = ({ label, value, className }) => {
  return (
    <div className={cn('mb-1 flex justify-between', className)}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
};

const SummaryDetailsRow = ({ label, value, className }) => {
  return (
    <li
      className={cn(
        'mb-1 flex justify-between gap-4 text-sm text-red-500',
        className
      )}
    >
      <span>{label}</span>
      <span>{value}</span>
    </li>
  );
};

const StatusButtonItem = ({ onClick, disabled, label, icon, active }) => {
  const Icon =
    typeof icon === 'string' ? (
      <span className="icon icon-outlined">{icon}</span>
    ) : (
      icon
    );

  return (
    <li
      className={cn(
        'mp-tab-navigation-item grid whitespace-nowrap',
        active && 'bg-accent text-white'
      )}
    >
      <button
        type="button"
        className={cn(
          'flex items-center justify-center gap-2',
          disabled && !active && 'opacity-50'
        )}
        disabled={disabled}
        onClick={onClick}
      >
        {Icon}
        {label}
      </button>
    </li>
  );
};
